import { action } from '@ember/object';
import type Owner from '@ember/owner';
import { service } from '@ember/service';
import { tracked } from '@glimmer/tracking';

import type {
	CreateRecordProperties,
	DSSetup,
	PopupData,
	Service,
	ValidationFunction,
} from 'webapp-types';
import { BlockVisualization } from 'webapp/enums/enums';

import BlocktypePickerComponent from 'webapp/components/blocktype-picker';
import CommonMetricAttributesUnitFieldsetComponent from 'webapp/components/common/metric-attributes-unit-fieldset';
import CommonUiIconComponent from 'webapp/components/common/ui-icon';
import type { Args } from 'webapp/components/popup/inner/modal';
import PopupInnerModalComponent from 'webapp/components/popup/inner/modal';
import SubmitButtonComponent from 'webapp/components/submit-button';

import DatawallGrid from 'webapp/models-custom/datawall-grid';
import MetricSetup from 'webapp/models-custom/metric-setup';
import type BlockRecord from 'webapp/models/block';

export default class ReportsPopupCreateBlockComponent extends PopupInnerModalComponent<PopupData.Report.CreateBlock> {
	@service('infolayer') declare infolayerService: Service.Infolayer;
	@service('request') declare requestService: Service.Request;
	@service('model-manager/blocks') declare mmBlocksService: Service.ModelManager.Blocks;
	@service('model-manager/reports') declare mmReportsService: Service.ModelManager.Reports;
	@service('model-manager/data-selects')
	declare mmDataSelectsService: Service.ModelManager.DataSelects;

	@tracked metricSetupData: DSSetup.MetricSetupData = {
		key: '',
		spaceAccessId: null,
	};
	@tracked blockVisualizationPickerShown = true;
	@tracked blockVisualization = BlockVisualization.BAR_CHART;

	validateFieldset: null | ValidationFunction = null;

	aiSummaryShowTrend: boolean | undefined;

	constructor(owner: Owner, args: Args) {
		super(owner, args);

		if (typeof this.popupData.blockVisualization !== 'undefined') {
			this.blockVisualization = this.popupData.blockVisualization;
		}
		if (typeof this.popupData.blockVisualizationPickerShown !== 'undefined') {
			this.blockVisualizationPickerShown = this.popupData.blockVisualizationPickerShown;
		}
		if (typeof this.popupData.blockData?.aiSummaryShowTrend !== 'undefined') {
			this.aiSummaryShowTrend = this.popupData.blockData.aiSummaryShowTrend;
		}
	}

	get report() {
		return this.popupData.report;
	}

	get metricSetup(): MetricSetup {
		return new MetricSetup(this.metricSetupData);
	}

	get isAttributed(): boolean {
		return this.metricSetup.isOutputMultipleAttributes;
	}

	get isNumber(): boolean {
		return this.blockVisualization === BlockVisualization.NUMBER;
	}

	@action
	changeBlockVisualization(blockVisualization: number): void {
		this.blockVisualization = blockVisualization;
	}

	@action
	bindFieldsetValidation(validationFn: ValidationFunction) {
		this.validateFieldset = validationFn;
	}

	@action
	changeMetricSetupData(newValue: DSSetup.MetricSetupData): void {
		this.metricSetupData = newValue;
	}

	@action
	save(): void {
		if (this.submitInProgress) {
			return;
		}

		if (!this.report) {
			return;
		}

		const metricSetup = new MetricSetup(this.metricSetupData);

		const params: CreateRecordProperties<BlockRecord> = {
			// no boardId: a report block has none, and the number attr serializes a missing one as null
			visualization: this.blockVisualization,
			order: 0,
			fromMetricSetup: metricSetup.metricSetupData ?? undefined, // the serializer skips both
			width: DatawallGrid.minimumSize * 2,
			height: DatawallGrid.minimumSize * 2,
			name: metricSetup.selectedMetricTitle,
		};

		if (!this.mmBlocksService.isVisualizationSeries(params.visualization!)) {
			// granulation only, the range comes from the metric (BE reads an empty range as none)
			params.fromMetricDateRange = {
				range: {},
				granulation: { isWholeRange: true },
			};
		}

		if (this.aiSummaryShowTrend !== undefined) {
			params.aiSummaryShowTrend = this.aiSummaryShowTrend;
		}

		const block = this.mmBlocksService.createNewModel(params);

		this.submitInProgress = true;

		const infolayer = this.infolayerService.loading('Creating datablock...');

		this.mmReportsService
			.createBlockForReport(this.report, block)
			.then(
				(blockRecord: BlockRecord) => {
					infolayer.ok('Datablock created!');
					this.popupData.onSuccess(blockRecord);
					this.close();
				},
				(error) => {
					infolayer.close();
					throw error;
				},
			)
			.finally(() => {
				this.submitInProgress = false;
			})
			.catch(this.requestService.showAllValidationErrors.bind(this.requestService));
	}
	<template>
		<div class="title">
			<CommonUiIconComponent @name="Metrics" @size="lg" />
			Select metric
		</div>

		<div class="content">

			<p><strong>Select a metric you would like to add to the report:</strong></p>

			<CommonMetricAttributesUnitFieldsetComponent
				@model={{this.metricSetupData}}
				@changeMetricSetupData={{this.changeMetricSetupData}}
				@validationBinder={{this.bindFieldsetValidation}}
				@requireSingleOutput={{false}}
				@onContentChange={{this.scheduleReposition}}
			/>

			{{#if this.blockVisualizationPickerShown}}
				<p><strong>How would you like to visualize data?</strong></p>
				<div class="row">
					<BlocktypePickerComponent
						@blockType={{this.blockVisualization}}
						@isAttributed={{this.isAttributed}}
						@onChangeBlockType={{this.changeBlockVisualization}}
					/>
				</div>
			{{/if}}

			<div class="controls2">
				<div class="controls2__right">
					<SubmitButtonComponent
						@text="Cancel"
						@theme="grey-empty"
						@noDelay={{true}}
						@onTrigger={{this.close}}
					/>
					<SubmitButtonComponent
						@text="Add to report"
						@extraClass="save"
						@onTrigger={{this.save}}
						@submitInProgress={{this.submitInProgress}}
					/>
				</div>
			</div>

		</div>
	</template>
}
